import { ComedianRepresentationContactRow } from "../../models/comedian-representation-contacts";
import createNoAuthSupabaseClient from "../../supabase/supabase";
import { logException } from "@punchup/sentry";

const TABLE = "comedian_representation_contacts";

/**
 * Public (unauthenticated) read of a comedian's representation contacts, in
 * display order. Uses the anon client and relies on the table's public-SELECT
 * RLS policy — this is the fan-facing Contacts tab, so no login is required.
 * Returns [] on error rather than throwing so the tab degrades gracefully.
 */
export async function getPublicComedianRepresentationContacts(
  comedianId: string
): Promise<ComedianRepresentationContactRow[]> {
  const supabase = createNoAuthSupabaseClient();
  const { data, error } = await supabase
    .from(TABLE)
    .select("*")
    .eq("comedian_id", comedianId)
    .order("sort_order", { ascending: true });

  if (error) {
    logException(error, {
      extra: { what: "getPublicComedianRepresentationContacts", comedianId },
    });
    return [];
  }
  return data ?? [];
}

/**
 * Whether a comedian has at least one representation contact. Drives the
 * conditional public "Contacts" nav tab (hidden until there is something to show).
 */
export async function comedianHasRepresentationContacts(
  comedianId: string
): Promise<boolean> {
  const supabase = createNoAuthSupabaseClient();
  const { count, error } = await supabase
    .from(TABLE)
    .select("id", { count: "exact", head: true })
    .eq("comedian_id", comedianId);

  if (error) {
    logException(error, {
      extra: { what: "comedianHasRepresentationContacts", comedianId },
    });
    return false;
  }
  return (count ?? 0) > 0;
}
